// src/yearbookRoutes.js
// Central route config for the parent yearbook portal.
// Project-scoped URLs are the source of truth for both active child/project and panel.

export const PROJECT_ROUTE_PREFIX = '/projects';

export const YEARBOOK_ROUTES = [
  {
    key: 'home',
    pathSegment: '',
    backendRoute: '',
    label: 'Overview',
    icon: '🏠',
    group: null,
    wide: false,
  },
  {
    key: 'photos',
    pathSegment: 'photos',
    backendRoute: 'photos',
    label: 'Personal Photos',
    icon: '📷',
    group: 'Customize Book',
    wide: true,
  },
  {
    key: 'personal_notes',
    pathSegment: 'personal-note',
    backendRoute: 'personal_note',
    label: 'Personal Note',
    icon: '💌',
    group: 'Customize Book',
    wide: false,
  },
  {
    key: 'memories',
    pathSegment: 'memories',
    backendRoute: 'memories',
    label: 'Memories',
    icon: '⭐',
    group: 'Customize Book',
    wide: false,
  },
  {
    key: 'school_photos',
    pathSegment: 'school-photos',
    backendRoute: 'school-photos',
    label: 'School Photos',
    icon: '🏫',
    group: 'Customize Book',
    wide: true,
  },
  {
    key: 'cover',
    pathSegment: 'cover',
    backendRoute: 'cover',
    label: 'Cover',
    icon: '📚',
    group: 'Customize Book',
    wide: false,
  },
  {
    key: 'cartoon',
    pathSegment: 'cartoon',
    backendRoute: 'cartoon',
    label: 'Cartoon',
    icon: '🧑‍🎨',
    group: 'Customize Book',
    wide: false,
  },
  {
    key: 'child',
    pathSegment: 'child-details',
    backendRoute: 'child-details',
    label: 'Child Details',
    icon: '✏️',
    group: 'Book Settings',
    wide: false,
  },
  {
    key: 'order',
    pathSegment: 'payment',
    backendRoute: 'payment',
    label: 'Order & Delivery',
    icon: '📦',
    group: 'Order & Delivery',
    wide: false,
  },
  // Backward-compatible URL alias. It is intentionally not attached to a
  // backend dashboard section, so Shipping + Payment render as one nav item.
  {
    key: 'order',
    pathSegment: 'shipping',
    backendRoute: null,
    label: 'Order & Delivery',
    icon: '📦',
    group: 'Order & Delivery',
    wide: false,
  },
];

export const YEARBOOK_HOME_ROUTE = YEARBOOK_ROUTES[0];

export function getRouteByKey(key) {
  return YEARBOOK_ROUTES.find((route) => route.key === key) || null;
}

export function getRouteByPathSegment(pathSegment = '') {
  return YEARBOOK_ROUTES.find((route) => route.pathSegment === pathSegment) || null;
}

export function getRouteByBackendRoute(backendRoute) {
  return YEARBOOK_ROUTES.find((route) => route.backendRoute === backendRoute) || null;
}

export function normalizePathname(pathname = '/') {
  if (!pathname || pathname === '/') return '/';

  const withoutTrailingSlash = pathname.replace(/\/+$/, '');
  return withoutTrailingSlash || '/';
}

export function buildProjectRoutePath(projectId, routeKey = YEARBOOK_HOME_ROUTE.key) {
  const route = getRouteByKey(routeKey) || YEARBOOK_HOME_ROUTE;

  if (!projectId) {
    return '/';
  }

  const encodedProjectId = encodeURIComponent(projectId);
  const basePath = `${PROJECT_ROUTE_PREFIX}/${encodedProjectId}`;

  return route.pathSegment ? `${basePath}/${route.pathSegment}` : basePath;
}

export function parseYearbookPath(pathname = '/') {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === '/') {
    return {
      isRoot: true,
      isValid: true,
      projectId: null,
      route: YEARBOOK_HOME_ROUTE,
    };
  }

  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments[0] !== PROJECT_ROUTE_PREFIX.replace(/^\//, '')) {
    return {
      isRoot: false,
      isValid: false,
      projectId: null,
      route: null,
    };
  }

  const encodedProjectId = segments[1];
  const pathSegment = segments[2] || '';

  if (!encodedProjectId || segments.length > 3) {
    return {
      isRoot: false,
      isValid: false,
      projectId: encodedProjectId ? safeDecodeURIComponent(encodedProjectId) : null,
      route: null,
    };
  }

  const route = getRouteByPathSegment(pathSegment);

  return {
    isRoot: false,
    isValid: Boolean(route),
    projectId: safeDecodeURIComponent(encodedProjectId),
    route,
  };
}

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (_err) {
    return value;
  }
}
